import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useUser } from '../contexts/UserContext';
import { useSound } from '../hooks/useSound';
import { categoryService } from '../services';
import type { Category } from '../types/Product';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartBounce, setCartBounce] = useState(false);
  const [cartFlash, setCartFlash] = useState(false);
  const [cartBurst, setCartBurst] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const { itemCount } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const { favorites } = useUser();
  const { play: playBoomSound } = useSound('/sounds/boom.mp3', { volume: 0.9 });

  // Load categories on mount
  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      if (!isMounted) return;
      
      try {
        setIsLoadingCategories(true);
        const response = await categoryService.getCategories();
        if (isMounted && response.success && response.data && response.data.length > 0) {
          // Take only the first 10 categories
          const topCategories = response.data.slice(0, 10);
          setCategories(topCategories);
        } else if (isMounted) {
          // If no categories, set empty array
          setCategories([]);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        if (isMounted) {
          setCategories([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };
    loadCategories();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for flying item reaching cart
  useEffect(() => {
    const handleFlyingItemReached = () => {
      // Trigger all animations
      setCartBounce(true);
      setCartFlash(true);
      setCartBurst(true);
      
      // Play boom sound effect
      playBoomSound();
      
      // Reset animations
      const bounceTimer = setTimeout(() => setCartBounce(false), 600);
      const flashTimer = setTimeout(() => setCartFlash(false), 800);
      const burstTimer = setTimeout(() => setCartBurst(false), 1200);
      
      // Cleanup function for this specific animation
      return () => {
        clearTimeout(bounceTimer);
        clearTimeout(flashTimer);
        clearTimeout(burstTimer);
      };
    };

    window.addEventListener('flyingItemReachedCart', handleFlyingItemReached);
    
    return () => {
      window.removeEventListener('flyingItemReachedCart', handleFlyingItemReached);
    };
  }, []);


  const SearchIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const CartIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  );

  const UserIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const MenuIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const ChevronDownIcon = () => (
    <svg className="w-4 h-4 ml-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: isCategoriesOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const HeartIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );

  return (
    <header className={`bg-white/95 backdrop-blur-md border-b border-neutral-200 fixed top-0 z-50 w-full transition-all duration-300 ${
      scrolled ? 'shadow-lg bg-white/98' : 'shadow-sm'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between lg:justify-start transition-all duration-300 ${
          scrolled ? 'h-14' : 'h-16'
        }`}>
          {/* Mobile Menu Button (Left side on mobile) */}
          <button 
            className="lg:hidden p-2 text-neutral-600 hover:text-neutral-900 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>

          {/* Mobile Logo - Visible only on mobile */}
          <Link 
            to="/" 
            className="lg:hidden flex items-center flex-1 ml-2"
          >
            <img 
              src="/images/logo/logo-transparent.png" 
              alt="Demo Crackers"
              className="h-10 w-auto"
              onError={(e) => {
                // Fallback to text if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <span className="hidden text-lg font-bold text-neutral-900">
              Demo<span className="text-accent-600">Crackers</span>
            </span>
          </Link>

          {/* Logo - Hidden on mobile, visible on desktop */}
          <Link 
            to="/" 
            className="hidden lg:flex items-center gap-3 group"
          >
            <img 
              src="/images/logo/logo-transparent.png" 
              alt="Demo Crackers" 
              className="h-12 w-auto group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                // Fallback to emoji if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <span className="hidden group-hover:animate-wiggle"></span>
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-neutral-900 group-hover:text-accent-600 transition-colors duration-300">
                Demo<span className="text-accent-600 group-hover:animate-pulse-glow">Crackers</span>
              </span>
              <span className="text-xs text-neutral-600 uppercase tracking-wider">
                Premium Quality Fireworks
              </span>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8 lg:ml-16">
            <Link 
              to="/" 
              className="text-neutral-700 hover:text-neutral-900 font-medium transition-colors duration-200"
            >
              Home
            </Link>
            
            <Link 
              to="/products" 
              className="text-neutral-700 hover:text-neutral-900 font-medium transition-colors duration-200"
            >
              All Products
            </Link>
            
            <div className="relative">
              <button 
                className="flex items-center text-neutral-700 hover:text-neutral-900 font-medium transition-colors duration-200"
                onMouseEnter={() => setIsCategoriesOpen(true)}
                onMouseLeave={() => setIsCategoriesOpen(false)}
              >
                Categories
                <ChevronDownIcon />
              </button>
              
              <div 
                className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-neutral-200 py-2 transition-all duration-200 z-[60] ${
                  isCategoriesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                }`}
                onMouseEnter={() => setIsCategoriesOpen(true)}
                onMouseLeave={() => setIsCategoriesOpen(false)}
              >
                {isLoadingCategories ? (
                  <div className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-600 mx-auto"></div>
                  </div>
                ) : (
                  <>
                    {categories.filter(cat => cat.name && cat.slug).map((category) => (
                      <Link 
                        key={category.slug} 
                        to={`/category/${category.slug}`} 
                        className="block px-4 py-3 text-sm text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 transition-colors duration-200"
                      >
                        {category.name}
                      </Link>
                    ))}
                    {categories.length === 0 && (
                      <div className="px-4 py-3 text-sm text-neutral-500 text-center">
                        No categories available
                      </div>
                    )}
                    {categories.length > 0 && (
                      <>
                        <div className="border-t border-neutral-200 my-2"></div>
                        <Link 
                          to="/categories" 
                          className="block px-4 py-3 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 transition-colors duration-200"
                        >
                          View All Categories →
                        </Link>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="relative inline-flex">
              {/* Continuous animation rings */}
              <div className="absolute inset-0 rounded-xl bg-accent-400 animate-ping opacity-30"></div>
              <div className="absolute inset-0 rounded-xl bg-accent-500 animate-ping opacity-20" style={{ animationDelay: '0.5s' }}></div>
              
              <Link 
                to="/quick-buy" 
                className="relative bg-accent-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-accent-700 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-accent-500/25 group overflow-hidden inline-flex items-center gap-2"
              >
                {/* Inner glow animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-accent-400/0 via-accent-400/30 to-accent-400/0 animate-shimmer"></div>
                
                <svg className="w-4 h-4 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
                <span className="relative z-10">Quick Buy</span>
                
                {/* Shimmer effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </Link>
            </div>
            
            <Link 
              to="/about" 
              className="text-neutral-700 hover:text-neutral-900 font-medium transition-colors duration-200"
            >
              About
            </Link>
            
            <Link 
              to="/contact" 
              className="text-neutral-700 hover:text-neutral-900 font-medium transition-colors duration-200"
            >
              Contact
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 lg:space-x-4 flex-shrink-0 lg:ml-auto">
            {/* Search Icon */}
            <Link 
              to="/products" 
              className="p-2 text-neutral-600 hover:text-neutral-900 transition-colors duration-200"
              title="Search Products"
            >
              <SearchIcon />
            </Link>

            {/* Favorites */}
            {isAuthenticated && (
              <Link 
                to="/favorites" 
                className="relative p-2 text-neutral-600 hover:text-neutral-900 transition-all duration-300 hover:scale-110 group"
              >
                <div className="relative">
                  <HeartIcon />
                  {favorites.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 group-hover:animate-pulse">
                      {favorites.length > 9 ? '9+' : favorites.length}
                    </span>
                  )}
                </div>
              </Link>
            )}

            {/* Cart */}
            <Link 
              to="/cart" 
              data-cart-icon
              className={`relative p-2 text-neutral-600 hover:text-neutral-900 transition-all duration-300 hover:scale-110 group ${
                cartBounce ? 'animate-bounce-soft' : ''
              } ${
                cartFlash ? 'animate-flash' : ''
              }`}
            >
              <div className="relative">
                <CartIcon />
                {itemCount > 0 && (
                  <span className={`absolute -top-2 -right-2 bg-accent-600 text-white text-xs font-bold ${
                    itemCount > 999 ? 'w-10 h-6 rounded-lg text-[10px]' : 
                    itemCount > 99 ? 'w-8 h-5 rounded-lg' : 
                    'w-5 h-5 rounded-full'
                  } flex items-center justify-center transition-all duration-300 group-hover:animate-pulse-glow ${
                    cartBounce ? 'animate-elastic' : ''
                  }`}>
                    {itemCount >= 10000 ? '9999+' : itemCount}
                  </span>
                )}
                {itemCount > 0 && (
                  <div className="absolute inset-0 bg-accent-500/20 rounded-full animate-ping opacity-75"></div>
                )}
                
                {/* Lightning Flash Effect */}
                {cartFlash && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -inset-4 bg-accent-300/80 rounded-full animate-lightning-flash"></div>
                    <div className="absolute -inset-2 bg-white/90 rounded-full animate-lightning-flash" style={{ animationDelay: '100ms' }}></div>
                  </div>
                )}
                
                {/* Crackers Burst Animation */}
                {cartBurst && (
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Sparkle particles */}
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-1 h-1 bg-accent-400 rounded-full animate-sparkle-burst"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: `rotate(${i * 45}deg)`,
                          animationDelay: `${i * 50}ms`
                        }}
                      />
                    ))}
                    
                    {/* Firework particles */}
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={`firework-${i}`}
                        className="absolute w-0.5 h-0.5 bg-accent-500 rounded-full animate-firework-burst"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: `rotate(${i * 30}deg)`,
                          animationDelay: `${i * 25}ms`
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Link>
            
            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button 
                  className="flex items-center space-x-2 p-2 text-neutral-600 hover:text-neutral-900 transition-colors duration-200"
                  onMouseEnter={() => setIsUserMenuOpen(true)}
                  onMouseLeave={() => setIsUserMenuOpen(false)}
                >
                  <UserIcon />
                  <span className="hidden sm:block text-sm font-medium">
                    {user?.phone?.slice(-4)}
                  </span>
                </button>
                
                <div 
                  className={`absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-neutral-200 py-2 transition-all duration-200 z-[60] ${
                    isUserMenuOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                  }`}
                  onMouseEnter={() => setIsUserMenuOpen(true)}
                  onMouseLeave={() => setIsUserMenuOpen(false)}
                >
                  <div className="px-4 py-2 border-b border-neutral-100">
                    <p className="text-sm font-medium text-neutral-900">+91 {user?.phone}</p>
                  </div>
                  <Link 
                    to="/profile"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 transition-colors duration-200"
                  >
                    <UserIcon />
                    My Profile
                  </Link>
                  <Link 
                    to="/enquiries"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    My Enquiries
                  </Link>
                  <Link 
                    to="/favorites"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    Favorites
                  </Link>
                  <div className="border-t border-neutral-100 mt-2 pt-2">
                    <button 
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="bg-accent-600 text-white hover:bg-accent-700 hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300 hover:scale-105 flex items-center gap-2
                           p-2 rounded-lg sm:px-5 sm:py-2.5 sm:rounded-xl sm:font-medium"
                title="Sign In"
              >
                <UserIcon />
                <span className="hidden sm:inline">Sign In</span>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-neutral-200 py-4 space-y-2">
            {/* Logo in mobile menu */}
            <Link 
              to="/" 
              className="flex items-center gap-3 px-3 py-4 mb-2 border-b border-neutral-100"
              onClick={() => setIsMenuOpen(false)}
            >
              <img
                src="/images/logo/logo-transparent.png"
                alt="Demo Crackers"
                className="h-12 w-auto"
                onError={(e) => {
                  // Fallback to emoji if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <span className="hidden text-3xl"></span>
              <div className="flex flex-col">
                <div className="text-xl font-bold text-neutral-900">
                  Demo<span className="text-accent-600">Crackers</span>
                </div>
                <span className="text-xs text-neutral-600 uppercase">
                  Premium Quality Fireworks
                </span>
              </div>
            </Link>
            
            <Link 
              to="/" 
              className="block px-3 py-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            
            <Link 
              to="/products" 
              className="block px-3 py-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              All Products
            </Link>
            
            <div className="space-y-1">
              <button 
                className="flex items-center justify-between w-full px-3 py-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
                onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
              >
                Categories
                <ChevronDownIcon />
              </button>
              
              {isCategoriesOpen && (
                <div className="pl-6 space-y-1">
                  {isLoadingCategories ? (
                    <div className="px-3 py-4 text-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent-600 mx-auto"></div>
                    </div>
                  ) : (
                    <>
                      {categories.filter(cat => cat.name && cat.slug).map((category) => (
                        <Link 
                          key={category.slug} 
                          to={`/category/${category.slug}`} 
                          className="block px-3 py-2 text-sm text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {category.name}
                        </Link>
                      ))}
                      {categories.length === 0 && (
                        <div className="px-3 py-2 text-sm text-neutral-500 text-center">
                          No categories available
                        </div>
                      )}
                      {categories.length > 0 && (
                        <Link 
                          to="/categories" 
                          className="block px-3 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          View All Categories →
                        </Link>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="relative">
              {/* Continuous animation rings for mobile */}
              <div className="absolute inset-0 rounded-lg bg-accent-400 animate-ping opacity-30"></div>
              <div className="absolute inset-0 rounded-lg bg-accent-500 animate-ping opacity-20" style={{ animationDelay: '0.5s' }}></div>
              
              <Link 
                to="/quick-buy" 
                className="relative px-3 py-2 bg-accent-600 text-white font-bold rounded-lg hover:bg-accent-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center gap-2 overflow-hidden"
                onClick={() => setIsMenuOpen(false)}
              >
                {/* Inner glow animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-accent-400/0 via-accent-400/30 to-accent-400/0 animate-shimmer"></div>
                
                <svg className="w-4 h-4 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
                <span className="relative z-10">Quick Buy</span>
              </Link>
            </div>
            
            <Link 
              to="/about" 
              className="block px-3 py-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            
            <Link 
              to="/contact" 
              className="block px-3 py-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 rounded-lg transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
            
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;